import React, { useContext, useEffect, useState } from 'react';

import {
  Grid,
  Typography,
} from '@material-ui/core';

import {
  useHistory,
  withRouter,
} from 'react-router-dom';

import PropTypes from 'prop-types';

import AlertDialog from '../../modals/alertDialog';
import BasicButton from '../../buttons/basicButton';
import BreadCrumb from '../../fields/breadCrumb';
import CliniciansList from './cliniciansList';
import CliniciansListBasic from './cliniciansListBasic';
import CustomSnackbar from '../../feedback/customSnackbar';
import Footer from '../../fields/footer';
import Header from '../../fields/header';
import MenuDrawer from '../../fields/menuDrawer';
import SpinnerLoading from '../../feedback/spinnerLoading';

import { deleteClinician, getClinicians, getClinicianPrograms, getEnrolments, getPrograms } from '../../../utils/comms';

import translations from '../../../languages/i18n';

import generalStyles from '../../../styles/generalStyles';
import { sectionTitle } from '../../../styles/formElementsStyles';

// NOTA: Per poder emprar el react-router-dom i enllaçar pàgines s'ha de convertir el component en una funció i exportar-lo embolicat per withRouter
// En cas de que el component sigui un fill directe de Route no fa falta fer-ho
// Explicat al punt 2.1. Using withRouter de https://dev.to/projectescape/programmatic-navigation-in-react-3p1l

import { SessionContext } from '../../../utils/context';

function ListClinicians({ lang, handleChangeLang }) {
  const sessionContext = useContext(SessionContext);

  const classes = generalStyles();
  const sectionTitleStyle = sectionTitle();

  const [cliniciansList, setCliniciansList] = useState([]);
  const [clinicianEnrolments, setClinicianEnrolments] = useState([]);
  const [feedbackType, setFeedbackType] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isProgramAdministrator, setIsProgramAdministrator] = useState(false);
  const [obtainClinicians, setObtainClinicians] = useState(false);
  const [selectedClinicians, setSelectedClinicians] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [typeOfClinician, setTypeOfClinician] = useState('');

  const history = useHistory();

  const manageFeedback = (fbType, fbMessage) => {
    setFeedbackMessage(translations[`_${fbType}${fbMessage}`]);
    setFeedbackType(fbType);
    setShowFeedback(true);
  };

  // Aquesta funció ja no és necessària ara els programes de cada clínic venen dins la resposta a getClinicians(). Deix el codi aquí per tenir-ho a mà si el torn necessitar
  /* const addCliniciansPrograms = async (clinicians) => {
    // Obtenim els ids dels clínics
    const cliniciansIds = clinicians.map((clinician) => clinician._id);
    // Demanam els programes de cada clínic
    const responseClinicianPrograms = await Promise.all(cliniciansIds.map((idClinician) => getClinicianPrograms(
      idClinician,
      sessionContext.userToken,
    )));
    const cliniciansWithPrograms = [];
    // L'array amb les respostes de Promise.all manté l'ordre de l'array amb les promises que se li ha passat.
    // És a dir, el primer element a l'array de respostes es correspon a la resposta del primer element de l'array d'entrada i així successivament
    responseClinicianPrograms.map((answer, index) => {
      let clinicianWithProgramsObj = {};
      if (answer.status && answer.status === 200) { // Si la consulta ha anat bé, cream l'objecte clínic corresponent amb l'array de programes rebut
        clinicianWithProgramsObj = { ...clinicians[index], programs: answer.data.programs };
      } else { // Si la consulta no ha anat bé, cream l'objecte clínic corresponent amb l'array de programes a null per tractar el cas en mostrar-ho
        clinicianWithProgramsObj = { ...clinicians[index], programs: null };
      }
      cliniciansWithPrograms[index] = clinicianWithProgramsObj;
    });
    return cliniciansWithPrograms;
  }; */

  const getTypeOfClinician = async () => {
    const response = await getPrograms(sessionContext.userToken); // Demanam el llistat de programes als que té accés el clínic
    if (response.status === 200) { // Tenim info del programa
      // Agafam els ids dels administradors del programa
      // Obtenim per a cada programa un array d'objectes amb la informació dels clínics que l'administren
      const progAdministrators = response.data.programs.map((program) => program.accessibleBy.clinicians.filter((clinician) => clinician.admin === true));
      // Dins progAdministrators podrem tenir un array de varies dimensions, amb flatMap ho passam a un array d'una dimensió
      const progAdministratorOnlyIds = progAdministrators.flatMap((program) => program.map((administrator) => administrator.clinician._id));
      // Eliminam els ids duplicats
      const progAdministratorIds = [...new Set(progAdministratorOnlyIds)];
      // Cercar dins progAdministratorIds si hi ha l'id del clinic
      return progAdministratorIds.find((administratorId) => administratorId === sessionContext.userId) ? 'Administrator' : 'Other';
    }
    if (response === 'timeOut') { // El servidor està caigut o l'usuari no té connexió
      manageFeedback('error', 'TimeOut');
    } else { // Error amb les dades o al servidor
      manageFeedback('error', 'Generic');
    }
    // En cas de que s'hagi produit un error en la comunicació i no poguem saber quin tipus d'usuari és, tornam Other
    return 'Other';
  };

  useEffect(() => {
    const getCliniciansFromServer = async () => {
      setObtainClinicians(false);
      setShowLoading(true);
      const cliniciansFromServer = await getClinicians(sessionContext.userToken);
      if (cliniciansFromServer.status === 200) { // Tenim llistat
        const { clinicians } = cliniciansFromServer.data;
        if (sessionContext.role === 'Clinician') {
          let clinicianType = await getTypeOfClinician();
          setIsProgramAdministrator(clinicianType === 'Administrator' || false);
          if (clinicianType === 'Administrator') { // Si administra algún programa veu el llistat de tots els clínics del sistema o els clínics que administra i/o supervisa
            setCliniciansList(clinicians);            
            // Per a un clínic, getEnrolments només torna els enrolments dels que en forma part (el clínic està dins el supervisedBy)
            // necessitarem els enrolments del clínic a CliniciansList per mostrar els clínics que supervisa
            const enrolments = await getEnrolments(sessionContext.userToken);
            if (enrolments.status === 200) { // Tenim enrolments
              setClinicianEnrolments(enrolments.data.enrolments);
            } else if (enrolments === 'timeOut') { // El servidor està caigut o l'usuari no té connexió
              manageFeedback('error', 'TimeOut');
            } else { // Error amb les dades o al servidor
              manageFeedback('error', 'Generic');
            }
          } else { // Es mostren els clinics que supervisa o amb qui comparteix algun equip clínic (clínic supervisor) o només amb qui comparteix equip (clinic basic)
            // Per a un clínic, getEnrolments només torna els enrolments dels que en forma part (el clínic està dins el supervisedBy)
            // necessitarem els enrolments del clínic a CliniciansList per mostrar els clínics que supervisa
            const enrolments = await getEnrolments(sessionContext.userToken);
            if (enrolments.status === 200) { // Tenim enrolments
              setClinicianEnrolments(enrolments.data.enrolments);
              // Comprovam si el clínic supervisa algún enrolment
              // flatMap torna un array d'una dimensió, ja que el map de supervisedBy retorna arrays de diferent longitud
              const supervisionsArray = enrolments.data.enrolments.flatMap((enrolment) => {
                return enrolment.supervisedBy.map((member) => member.clinician === sessionContext.userId && member.admin === true);
              });
              const isSupervisor = supervisionsArray.find((element) => element === true) || false; // Si el find dona undefined, isSupervisor serà false
              clinicianType = isSupervisor ? 'Supervisor' : 'Basic';
              // Ficam dins un array els ids dels clinics de l'equip de cada enrolment
              // flatMap torna un array d'una dimensió, ja que el map de supervisedBy retorna arrays de diferent longitud
              const teamMembers = enrolments.data.enrolments.flatMap((enrolment) => {
                return enrolment.supervisedBy.map((member) => member.clinician);
              });
              // Eliminam els ids duplicats
              const clinicianIds = [...new Set(teamMembers)];
              // Filtram el llistat de clínics rebuts del servidor per només quedar-mos amb els clínics amb el que es comparteix enrolment
              const filteredClinicians = clinicians.filter((clinician) => clinicianIds.some((clinicianId) => clinicianId === clinician._id));
              // Si el clínic no comparteix enrolment amb cap altre clínic, només mostram les seves dades
              if (clinicians.length > 0 && filteredClinicians.length === 0) filteredClinicians.push(clinicians.find((clinician) => clinician._id === sessionContext.userId));
              setCliniciansList(filteredClinicians);
            } else if (enrolments === 'timeOut') { // El servidor està caigut o l'usuari no té connexió
              manageFeedback('error', 'TimeOut');
            } else { // Error amb les dades o al servidor
              manageFeedback('error', 'Generic');
            }
          }
          setTypeOfClinician(clinicianType);
        } else { // És administrador del sistema
          setCliniciansList(clinicians);
        }
      } else if (cliniciansFromServer === 'timeOut') { // El servidor està caigut o l'usuari no té connexió
        manageFeedback('error', 'TimeOut');
      } else { // Error amb les dades o al servidor
        manageFeedback('error', 'Generic');
      }
      setShowLoading(false);
    };
    getCliniciansFromServer();
  }, [obtainClinicians]);

  const deleteClinicians = async () => {
    setShowLoading(true);
    // Esborram usuaris
    //const responseDelete = await Promise.all(selectedClinicians.map((element) => deleteClinician(element.id, sessionContext.userToken)));
    const responseDelete = await Promise.all(selectedClinicians.map(async (element) => await deleteClinician(element.id, sessionContext.userToken)));

    // Comprovam si dins les respostes n'hi ha alguna de timeOut o erronia
    const allOk = responseDelete.map((answer) => {
      if (answer.status && answer.status === 200) return 'ok';
      if (answer === 'timeOut') return 'timeOut';
      if (answer === 409) return 'conflict';
      return 'error';
    });
    const idTO = allOk.indexOf('timeOut');
    const idC = allOk.indexOf('conflict');
    const idE = allOk.indexOf('error');
    if (idTO > -1 || idC > -1 || idE > -1) { // Al manco hi ha 1 timeOut, 1 conflicte o 1 error
      // Miram si algun dels errors rebuts és un timeOut
      if (idTO > -1) { // Hi ha hagut un timeOut
        manageFeedback('error', 'TimeOut');
      } else if (idC > -1) { // Hi ha hagut un conflicte
        manageFeedback('error', 'DeleteClinicianConflict');
      } else { // Hi ha hagut un error
        manageFeedback('error', 'DeleteClinician');
      }
    } else { // No hi ha cap error
      manageFeedback('success', 'DeleteClinician');
    }
    setSelectedClinicians([]);
    // Indicam al useEffect que es torni a executar
    setObtainClinicians(true);
  };

  const viewClinician = (id) => {
    history.push(`/${sessionContext.role.toLowerCase()}/clinicianInfo/${id}`);
  };

  const viewClinicianSchedules = (id) => {
    history.push(`/${sessionContext.role.toLowerCase()}/clinicianSchedulesInfo/${id}`);
  };

  const newClinician = () => {
    history.push(`/${sessionContext.role.toLowerCase()}/newClinicianInfo`);
  };

  const selectedClinician = (id, username) => {
    const selectedCliniciansCopy = selectedClinicians.slice();
    // Cercam si dins selectedCliniciansCopy hi ha el valor de clinician
    const idx = selectedCliniciansCopy.findIndex((item) => item.id === id);
    if (idx < 0) selectedCliniciansCopy.push({ id, username }); // Si idx és -1 vol dir que no ha trobat el valor, l'afegim a la taula
    else selectedCliniciansCopy.splice(idx, 1); // L'ha trobat l'eliminam de la taula
    setSelectedClinicians(selectedCliniciansCopy);
  };

  const gotoScreen = (screen) => {
    history.push(`/${sessionContext.role.toLowerCase()}/${screen}`);
  };

  const showMenuDrawer = (show) => {
    setShowDrawer(show);
  };

  const manageDeleteAlertAnswer = (agree) => {
    setShowDeleteAlert(false);
    if (agree) deleteClinicians();
  };

  return (
    <div className={classes.content}>
      <SpinnerLoading show={showLoading} />
      {/* ATENCIÓ: el role ve del servidor amb la primera lletra en majuscula, jo ho necessit tot en minuscula */}
      <Header type={sessionContext.role.toLowerCase()} handleShowDrawer={(show) => showMenuDrawer(show)} lang={lang} handleChangeLang={handleChangeLang} />
      <MenuDrawer
        type={sessionContext.role.toLowerCase()}
        showDrawer={showDrawer}
        handleShowDrawer={(show) => showMenuDrawer(show)}
        handleGotoScreen={(screen) => gotoScreen(screen)}
      />
      <AlertDialog
        open={showDeleteAlert}
        handleClose={(agree) => manageDeleteAlertAnswer(agree)}
        title={translations['_deleteCliniciansAlertTitle']}
        message={translations['_deleteCliniciansAlertMessage']}
        data={selectedClinicians} field='username'
      />
      <CustomSnackbar
        show={showFeedback}
        handleCloseFeedback={(show) => setShowFeedback(show)}
        type={feedbackType}
        message={(showFeedback && feedbackMessage) || ''}
      />
      <div className={classes.body}>
        <Grid container direction='column' spacing={2}>
          {/* ----------------------- Fila 0 ----------------------- */}
          <Grid item xs={12}>
            <BreadCrumb role={sessionContext.role.toLowerCase()} home='listClinicians' lang={lang} />
          </Grid>
          {/* ----------------------- Fila 1 ----------------------- */}
          <Grid item className={sectionTitleStyle.root}>
            <Grid container justify='space-between' spacing={2}>
              <Grid item>
                <Typography variant='h4' component='div' className={sectionTitleStyle.title}>{sessionContext.role === 'Admin' ? translations['_allClinicians'] : translations['_clinicians']}</Typography>
              </Grid>
              <Grid item>
                {sessionContext.role === 'Admin' && <BasicButton handleClick={() => newClinician()} type='accept' text={translations['_newClinician']} />}
              </Grid>
            </Grid>
          </Grid>
          {/* ----------------------- Fila 2 ----------------------- */}
          <Grid item>
            <Grid container justify='space-between' spacing={2}>
              <Grid item xs={12}>
                {(cliniciansList.length === 0 && sessionContext.role !== 'Admin') ? (
                  <Typography variant='h6' component='div' className={sectionTitleStyle.title}>
                    {translations['_emptyPatients']}
                  </Typography>
                ) : (
                  ((typeOfClinician === 'Administrator' || typeOfClinician === 'Supervisor') && (
                    <CliniciansList
                      cliniciansData={cliniciansList}
                      clinicianEnrolments={clinicianEnrolments}
                      isProgramAdministrator={isProgramAdministrator}
                      selectedClinicians={selectedClinicians}
                      handleViewClinician={(clinician) => viewClinician(clinician)}
                      handleViewClinicianSchedules={(clinician) => viewClinicianSchedules(clinician)}
                      handleDelete={() => setShowDeleteAlert(true)}
                      handleSelectedCliniciansData={(id, username) => selectedClinician(id, username)}
                    />
                  )) || (
                    <CliniciansListBasic
                      cliniciansData={cliniciansList}
                      isProgramAdministrator={isProgramAdministrator}
                      selectedClinicians={selectedClinicians}
                      handleViewClinician={(clinician) => viewClinician(clinician)}
                      handleViewClinicianSchedules={(clinician) => viewClinicianSchedules(clinician)}
                      handleDelete={() => setShowDeleteAlert(true)}
                      handleSelectedCliniciansData={(id, username) => selectedClinician(id, username)}
                    />
                  )
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
      <Footer />
    </div>
  );
}

export default withRouter(ListClinicians);

// Indica i controla els props que se han de passar al component
ListClinicians.propTypes = {
  handleChangeLang: PropTypes.func.isRequired,
  lang: PropTypes.string.isRequired,
};
